import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mic, MicOff, Send, Square, Clock, User, CheckCircle,
  ChevronDown, Truck, MapPin, AlertTriangle, Filter, Loader2,
  MessageSquare, Edit, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  dispatch: { label: 'Dispatch', color: 'blue' },
  maintenance: { label: 'Maintenance', color: 'orange' },
  general: { label: 'General', color: 'gray' },
  urgent: { label: 'Urgent', color: 'red' },
  training: { label: 'Training', color: 'green' },
  admin: { label: 'Admin', color: 'purple' }
};

const PRIORITY_COLORS = {
  low: 'text-gray-400',
  medium: 'text-yellow-400',
  high: 'text-orange-400',
  critical: 'text-red-400'
};

export default function VoiceAssistant() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState('general');
  const [notes, setNotes] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [displayName, setDisplayName] = useState(() => {
    return localStorage.getItem('hub_voice_display_name') || '';
  });
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentingOn, setCommentingOn] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editForm, setEditForm] = useState({ serbian: '', english: '', category: '', priority: '' });
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchNotes();
  }, [filterCategory]);

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const params = filterCategory !== 'all' ? `?category=${filterCategory}` : '';
      const res = await axios.get(`${API}/voice-assistant/notes${params}`, { withCredentials: true });
      setNotes(res.data.notes || []);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) { resolve(null); return; }
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(blob);
      };
      
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      clearInterval(timerRef.current);
      setIsRecording(false);
    });
  };

  const handleStopAndSend = async () => {
    const blob = await stopRecording();
    if (!blob || blob.size < 1000) {
      toast.error('Recording too short. Hold longer.');
      return;
    }

    // Process silently in background - no visible loading state
    const formData = new FormData();
    formData.append('file', blob, 'voice_note.webm');
    formData.append('channel', selectedChannel);
    if (displayName.trim()) {
      formData.append('display_name', displayName.trim());
    }

    // Fire and forget - user can immediately record another
    axios.post(`${API}/voice-assistant/transcribe`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    }).then(res => {
      toast.success('Sent');
      setNotes(prev => [res.data, ...prev]);
    }).catch(err => {
      toast.error(err.response?.data?.detail || 'Failed to send');
    });
  };

  const acceptNote = async (noteId) => {
    try {
      const res = await axios.post(`${API}/voice-assistant/notes/${noteId}/accept`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const completeNote = async (noteId) => {
    try {
      await axios.post(`${API}/voice-assistant/notes/${noteId}/complete`, {}, { withCredentials: true });
      toast.success('Task completed!');
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    }
  };

  const addComment = async (noteId) => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/voice-assistant/notes/${noteId}/comment`, 
        { text: commentText.trim() }, { withCredentials: true });
      toast.success('Comment added');
      setCommentText('');
      setCommentingOn(null);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    }
  };

  const saveEdit = async (noteId) => {
    try {
      await axios.put(`${API}/voice-assistant/notes/${noteId}`, editForm, { withCredentials: true });
      toast.success('Updated');
      setEditingNote(null);
      fetchNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (iso) => {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="voice-assistant">
      {/* Recording Section */}
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <Mic className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">Voice Assistant</h2>
            <p className="text-xs text-muted-foreground">Record a message in Serbian or English</p>
          </div>
          <button
            onClick={() => setShowNameEdit(!showNameEdit)}
            className="text-xs px-3 py-1.5 rounded-lg bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            data-testid="edit-display-name-btn"
          >
            {displayName || 'Set Slack Name'}
          </button>
        </div>

        {/* Display Name Editor */}
        {showNameEdit && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-secondary/30 border border-border/30">
            <User className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Your Slack display name (e.g. Stefan Buddy)"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                localStorage.setItem('hub_voice_display_name', e.target.value);
              }}
              className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border/30 text-sm focus:border-accent outline-none"
              data-testid="display-name-input"
            />
            <button
              onClick={() => setShowNameEdit(false)}
              className="px-3 py-2 rounded-lg bg-accent/20 text-accent text-sm font-medium hover:bg-accent/30"
            >
              Done
            </button>
          </div>
        )}

        {/* Channel Selector */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-sm text-muted-foreground">Channel:</span>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(CATEGORIES).map(([key, val]) => (
              <button
                key={key}
                onClick={() => setSelectedChannel(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedChannel === key
                    ? `bg-${val.color}-500/30 text-${val.color}-400 border border-${val.color}-500/50`
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                }`}
                data-testid={`channel-${key}`}
              >
                {val.label}
              </button>
            ))}
          </div>
        </div>

        {/* Record Button */}
        <div className="flex flex-col items-center gap-4">
          {isRecording ? (
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={handleStopAndSend}
                className="w-24 h-24 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all shadow-lg shadow-red-500/30 animate-pulse"
                data-testid="stop-recording-btn"
              >
                <Square className="w-10 h-10 text-white fill-white" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-400 font-mono text-lg">{formatTime(recordingTime)}</span>
              </div>
              <p className="text-sm text-muted-foreground">Recording... Tap to stop & send</p>
            </div>
          ) : (
            <button
              onClick={startRecording}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 flex items-center justify-center transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-105 active:scale-95"
              data-testid="start-recording-btn"
            >
              <Mic className="w-10 h-10 text-white" />
            </button>
          )}
          {!isRecording && (
            <p className="text-sm text-muted-foreground">Tap to start recording</p>
          )}
        </div>
      </div>

      {/* Notes History */}
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-accent" />
            Voice Notes
          </h3>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-secondary border border-border/30 text-sm"
              data-testid="filter-category"
            >
              <option value="all">All</option>
              {Object.entries(CATEGORIES).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loadingNotes ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12">
            <Mic className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No voice notes yet</p>
            <p className="text-sm text-muted-foreground/70">Record your first message above</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {notes.map((note) => {
              const cat = CATEGORIES[note.category] || CATEGORIES.general;
              return (
                <div
                  key={note.note_id}
                  className={`p-4 rounded-xl border transition-colors ${
                    note.status === 'completed' 
                      ? 'bg-green-500/5 border-green-500/20' 
                      : note.status === 'accepted'
                      ? 'bg-blue-500/5 border-blue-500/20'
                      : note.priority === 'critical' || note.priority === 'high'
                      ? 'bg-red-500/5 border-red-500/20'
                      : 'bg-secondary/30 border-border/30'
                  }`}
                  data-testid={`voice-note-${note.note_id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${cat.color}-500/20 text-${cat.color}-400`}>
                        {cat.label}
                      </span>
                      <span className={`text-xs font-medium ${PRIORITY_COLORS[note.priority] || ''}`}>
                        {note.priority?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(note.created_at)}</span>
                  </div>

                  {/* Content - Bilingual */}
                  <div className="space-y-2 mb-3">
                    <div className="flex gap-2">
                      <span className="text-xs font-bold text-blue-400 mt-0.5 shrink-0">SR</span>
                      <p className="text-sm">{note.serbian}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-xs font-bold text-green-400 mt-0.5 shrink-0">EN</span>
                      <p className="text-sm text-muted-foreground">{note.english}</p>
                    </div>
                  </div>

                  {/* Extracted Info */}
                  {note.extracted && (note.extracted.trucks?.length > 0 || note.extracted.locations?.length > 0) && (
                    <div className="flex gap-3 mb-3 flex-wrap">
                      {note.extracted.trucks?.map((t, i) => (
                        <span key={i} className="flex items-center gap-1 text-xs bg-accent/10 text-accent px-2 py-1 rounded">
                          <Truck className="w-3 h-3" /> {t}
                        </span>
                      ))}
                      {note.extracted.locations?.map((l, i) => (
                        <span key={i} className="flex items-center gap-1 text-xs bg-purple-500/10 text-purple-400 px-2 py-1 rounded">
                          <MapPin className="w-3 h-3" /> {l}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer: Status + Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span>{note.user_name}</span>
                      {note.accepted_by_name && (
                        <>
                          <span>|</span>
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          <span className="text-green-400">{note.accepted_by_name}</span>
                        </>
                      )}
                      {note.status === 'completed' && (
                        <span className="text-green-400 font-medium ml-1">DONE</span>
                      )}
                      {note.edited_by && (
                        <span className="text-muted-foreground/60 italic ml-1">edited by {note.edited_by}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => {
                        setCommentingOn(commentingOn === note.note_id ? null : note.note_id);
                        setEditingNote(null);
                      }} className="p-1.5 rounded-lg hover:bg-secondary/50 text-muted-foreground hover:text-foreground">
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => {
                        if (editingNote === note.note_id) {
                          setEditingNote(null);
                        } else {
                          setEditingNote(note.note_id);
                          setEditForm({ serbian: note.serbian, english: note.english, category: note.category, priority: note.priority });
                          setCommentingOn(null);
                        }
                      }} className="p-1.5 rounded-lg hover:bg-secondary/50 text-muted-foreground hover:text-foreground">
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      {note.status === 'open' && (
                        <Button size="sm" onClick={() => acceptNote(note.note_id)}
                          className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 h-7 px-3 text-xs"
                          data-testid={`accept-note-${note.note_id}`}
                        >
                          Accept
                        </Button>
                      )}
                      {note.status === 'accepted' && (
                        <Button size="sm" onClick={() => completeNote(note.note_id)}
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30 h-7 px-3 text-xs"
                          data-testid={`complete-note-${note.note_id}`}
                        >
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Edit Form */}
                  {editingNote === note.note_id && (
                    <div className="mt-3 p-3 rounded-xl bg-secondary/30 border border-border/30 space-y-2">
                      <div>
                        <label className="text-xs text-muted-foreground">Serbian</label>
                        <textarea value={editForm.serbian} onChange={e => setEditForm({...editForm, serbian: e.target.value})}
                          className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border/30 text-sm resize-none" rows={2} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">English</label>
                        <textarea value={editForm.english} onChange={e => setEditForm({...editForm, english: e.target.value})}
                          className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border/30 text-sm resize-none" rows={2} />
                      </div>
                      <div className="flex gap-2">
                        <select value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})}
                          className="flex-1 px-2 py-1.5 rounded-lg bg-secondary border border-border/30 text-xs">
                          {Object.entries(CATEGORIES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <select value={editForm.priority} onChange={e => setEditForm({...editForm, priority: e.target.value})}
                          className="flex-1 px-2 py-1.5 rounded-lg bg-secondary border border-border/30 text-xs">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setEditingNote(null)} className="px-3 py-1.5 text-xs rounded-lg bg-secondary">Cancel</button>
                        <button onClick={() => saveEdit(note.note_id)} className="px-3 py-1.5 text-xs rounded-lg bg-accent text-black font-medium">Save</button>
                      </div>
                    </div>
                  )}

                  {/* Comment Input */}
                  {commentingOn === note.note_id && (
                    <div className="mt-3 flex gap-2">
                      <input type="text" placeholder="Add a note..."
                        value={commentText} onChange={e => setCommentText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addComment(note.note_id); }}
                        className="flex-1 px-3 py-2 rounded-lg bg-secondary border border-border/30 text-sm"
                        autoFocus />
                      <button onClick={() => addComment(note.note_id)}
                        className="px-3 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Comments List */}
                  {note.comments?.length > 0 && (
                    <div className="mt-3 space-y-1.5 pl-3 border-l-2 border-border/30">
                      {note.comments.map(c => (
                        <div key={c.comment_id} className="text-xs">
                          <span className="font-medium text-accent">{c.user_name}</span>
                          <span className="text-muted-foreground mx-1">·</span>
                          <span className="text-muted-foreground/60">{formatDate(c.created_at)}</span>
                          <p className="text-muted-foreground mt-0.5">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
